import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  Button,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Computer as ComputerIcon,
  Apple as AppleIcon,
  Memory as MemoryIcon,
  Download as DownloadIcon,
  PlayArrow as PlayArrowIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import groupsApiService from '../../services/groups/GroupsApiService';
import dashboardApiService from '../../services/dashboard/DashboardApiService';

const DeployAgent = () => {
  const [selectedOS, setSelectedOS] = useState('linux');
  const [selectedArch, setSelectedArch] = useState('rpm-amd64');
  const [serverAddress, setServerAddress] = useState('34.237.68.57');
  const [agentName, setAgentName] = useState('');
  const [copiedCommand, setCopiedCommand] = useState('');
  const [joinManagerUser, setJoinManagerUser] = useState('');
  const [joinManagerPassword, setJoinManagerPassword] = useState('');
  
  // Group management states
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Agent assignment states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [assigningAgent, setAssigningAgent] = useState(false);

  useEffect(() => {
    fetchGroups();
    fetchAgents();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoadingGroups(true);
      const groupsData = await groupsApiService.getAllGroups();
      setGroups(groupsData || []);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      showSnackbar('Failed to load groups', 'error');
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const agentsData = await dashboardApiService.getAllAgents();
      setAgents(agentsData || []);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      showSnackbar('Please enter a group name', 'warning');
      return;
    }

    try {
      setCreatingGroup(true);
      await groupsApiService.createGroup(newGroupName.trim());
      showSnackbar(`Group '${newGroupName}' created successfully`, 'success');
      setNewGroupName('');
      setCreateGroupDialogOpen(false);
      await fetchGroups();
    } catch (error) {
      console.error('Failed to create group:', error);
      showSnackbar('Failed to create group', 'error');
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleAssignAgent = async () => {
    if (!selectedAgent || selectedGroups.length === 0) {
      showSnackbar('Please select an agent and at least one group', 'warning');
      return;
    }

    try {
      setAssigningAgent(true);
      // Assign agent to each selected group
      for (const groupId of selectedGroups) {
        await groupsApiService.assignAgentToGroup(selectedAgent, groupId, true);
      }
      showSnackbar(`Agent assigned to ${selectedGroups.length} group(s) successfully`, 'success');
      setAssignDialogOpen(false);
      setSelectedAgent('');
    } catch (error) {
      console.error('Failed to assign agent:', error);
      showSnackbar('Failed to assign agent to group', 'error');
    } finally {
      setAssigningAgent(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (groupId === 'default') {
      showSnackbar('Cannot delete the default group', 'warning');
      return;
    }

    if (window.confirm(`Are you sure you want to delete group '${groupId}'?`)) {
      try {
        await groupsApiService.deleteGroup(groupId);
        showSnackbar(`Group '${groupId}' deleted successfully`, 'success');
        setSelectedGroups(selectedGroups.filter(g => g !== groupId));
        await fetchGroups();
      } catch (error) {
        console.error('Failed to delete group:', error);
        showSnackbar('Failed to delete group', 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const steps = [
    'Select the package to download and install on your system',
    'Server address',
    'Optional settings',
    'Run the following commands to download and install the agent',
    'Start the agent'
  ];

  const osOptions = [
    { id: 'linux', name: 'LINUX', icon: <ComputerIcon /> },
    { id: 'windows', name: 'WINDOWS', icon: <MemoryIcon /> },
    { id: 'macos', name: 'macOS', icon: <AppleIcon /> },
    { id: 'kubernetes', name: 'KUBERNETES', icon: <SettingsIcon /> }
  ];

  const getInstallCommand = () => {
    const manager = `WAZUH_MANAGER='${serverAddress}'`;
    const agentNameEnv = agentName ? ` WAZUH_AGENT_NAME='${agentName}'` : '';
    const groupsEnv = selectedGroups.length > 0 ? ` WAZUH_AGENT_GROUP='${selectedGroups.join(',')}'` : '';
    const envVars = `${manager}${agentNameEnv}${groupsEnv}`;
    
    switch (selectedOS) {
      case 'linux':
        if (selectedArch === 'rpm-amd64') {
          return `curl -o wazuh-agent-4.12.0-1.x86_64.rpm https://packages.wazuh.com/4.x/yum/wazuh-agent-4.12.0-1.x86_64.rpm && sudo ${envVars} rpm -ihv wazuh-agent-4.12.0-1.x86_64.rpm`;
        } else if (selectedArch === 'rpm-aarch64') {
          return `curl -o wazuh-agent-4.12.0-1.aarch64.rpm https://packages.wazuh.com/4.x/yum/wazuh-agent-4.12.0-1.aarch64.rpm && sudo ${envVars} rpm -ihv wazuh-agent-4.12.0-1.aarch64.rpm`;
        } else if (selectedArch === 'deb-amd64') {
          return `wget https://packages.wazuh.com/4.x/apt/pool/main/w/wazuh-agent/wazuh-agent_4.12.0-1_amd64.deb && sudo ${envVars} dpkg -i ./wazuh-agent_4.12.0-1_amd64.deb`;
        } else if (selectedArch === 'deb-aarch64') {
          return `wget https://packages.wazuh.com/4.x/apt/pool/main/w/wazuh-agent/wazuh-agent_4.12.0-1_arm64.deb && sudo ${envVars} dpkg -i ./wazuh-agent_4.12.0-1_arm64.deb`;
        }
        break;
      case 'windows':
        const winAgentName = agentName ? ` WAZUH_AGENT_NAME="${agentName}"` : '';
        const winGroups = selectedGroups.length > 0 ? ` WAZUH_AGENT_GROUP="${selectedGroups.join(',')}"` : '';
        return `Invoke-WebRequest -Uri https://packages.wazuh.com/4.x/windows/wazuh-agent-4.12.0-1.msi -OutFile $env:tmp\\wazuh-agent; msiexec.exe /i $env:tmp\\wazuh-agent /q ${manager}${winAgentName}${winGroups}`;
      case 'macos':
        const macEnvFile = agentName || selectedGroups.length > 0 
          ? `echo "${manager}${agentNameEnv}${groupsEnv}" > /tmp/wazuh_envs && ` 
          : `echo "${manager}" > /tmp/wazuh_envs && `;
        if (selectedArch === 'intel') {
          return `curl -so wazuh-agent.pkg https://packages.wazuh.com/4.x/macos/wazuh-agent-4.12.0-1.intel64.pkg && ${macEnvFile}sudo installer -pkg ./wazuh-agent.pkg -target /`;
        } else if (selectedArch === 'apple') {
          return `curl -so wazuh-agent.pkg https://packages.wazuh.com/4.x/macos/wazuh-agent-4.12.0-1.arm64.pkg && ${macEnvFile}sudo installer -pkg ./wazuh-agent.pkg -target /`;
        }
        break;
      case 'kubernetes':
        return '# Use the Kubernetes DaemonSet manifest provided below on this page.';
      default:
        return '';
    }
  };

  const getStartCommand = () => {
    switch (selectedOS) {
      case 'linux':
        return `sudo systemctl daemon-reload\nsudo systemctl enable wazuh-agent\nsudo systemctl start wazuh-agent`;
      case 'macos':
        return `sudo /Library/Ossec/bin/wazuh-control start`;
      case 'windows':
        return `NET START WazuhSvc`;
      case 'kubernetes':
        return '# The agent runs as a DaemonSet in Kubernetes; no manual start command is required.';
      default:
        return '';
    }
  };

  const getKubernetesYaml = () => {
    const userValue = joinManagerUser || '';
    const passwordValue = joinManagerPassword || '';
    return (
`apiVersion: apps/v1
kind: DaemonSet
metadata:
  name: wazuh-agent
  namespace: wazuh
spec:
  selector:
    matchLabels:
      app: wazuh-agent
  template:
    metadata:
      labels:
        app: wazuh-agent
    spec:
      containers:
      - name: wazuh-agent
        image: kennyopennix/wazuh-agent
        env:
        - name: JOIN_MANAGER_PROTOCOL
          value: "https"
        - name: JOIN_MANAGER_MASTER_HOST
          value: "${serverAddress}"
        - name: JOIN_MANAGER_WORKER_HOST
          value: "${serverAddress}"
        - name: JOIN_MANAGER_USER
          value: "${userValue}"
        - name: JOIN_MANAGER_PASSWORD
          value: "${passwordValue}"
        - name: JOIN_MANAGER_API_PORT
          value: "55000"
        securityContext:
          privileged: true
        volumeMounts:
        - name: varlog
          mountPath: /var/log
        - name: docker-sock
          mountPath: /var/run/docker.sock
      volumes:
      - name: varlog
        hostPath:
          path: /var/log
      - name: docker-sock
        hostPath:
          path: /var/run/docker.sock`);
  };

  const copyToClipboard = async (text, commandType) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCommand(commandType);
      setTimeout(() => setCopiedCommand(''), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const getArchOptions = () => {
    if (selectedOS === 'kubernetes') {
      return [];
    } else if (selectedOS === 'windows') {
      return [{ id: 'x64', name: 'MSI 32/64 bits' }];
    } else if (selectedOS === 'macos') {
      return [
        { id: 'intel', name: 'Intel' },
        { id: 'apple', name: 'Apple Silicon' }
      ];
    } else {
      return [
        { id: 'rpm-amd64', name: 'RPM amd64' },
        { id: 'rpm-aarch64', name: 'RPM aarch64' },
        { id: 'deb-amd64', name: 'DEB amd64' },
        { id: 'deb-aarch64', name: 'DEB aarch64' }
      ];
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section with Gradient */}
      <Box 
        sx={{ 
          mb: 5,
          p: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.1
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px'
              }}
            >
              🚀
            </Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: '-0.5px' }}>
                Deploy New Agent
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                Install and configure OpenShield agents across your infrastructure
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Card 
        sx={{ 
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.06)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
          }
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {selectedOS === 'kubernetes' ? (
            <Stepper
              orientation="vertical"
              sx={{
                '& .MuiStepContent-root': {
                  borderLeft: '3px solid #e8eaf6',
                  ml: 2,
                  pl: 4,
                  mt: 1
                },
                '& .MuiStepLabel-root': {
                  py: 1.5
                },
                '& .MuiStepIcon-root': {
                  fontSize: '2rem',
                  '&.Mui-active': {
                    color: '#667eea'
                  },
                  '&.Mui-completed': {
                    color: '#4caf50'
                  }
                }
              }}
            >
              {/* Kubernetes Step 1: Server address */}
              <Step active={true} completed={false}>
                <StepLabel>
                  <Typography variant="h5" sx={{ fontWeight: '700', color: '#1a237e', letterSpacing: '-0.3px' }}>
                    🌐 Server address for Kubernetes manager
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    This is the address the agents running in your Kubernetes cluster will use to communicate with the Wazuh manager.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      label="Assign a server address"
                      value={serverAddress}
                      onChange={(e) => setServerAddress(e.target.value)}
                      sx={{ minWidth: 300 }}
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      Remember server address
                    </Typography>
                  </Box>
                </StepContent>
              </Step>

              {/* Kubernetes Step 2: API credentials */}
              <Step active={true} completed={false}>
                <StepLabel>
                  <Typography variant="h5" sx={{ fontWeight: '700', color: '#1a237e', letterSpacing: '-0.3px' }}>
                    🔐 Wazuh API credentials
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Optionally provide your Wazuh API credentials so they are prefilled in the manifest. You can also leave them empty and
                    edit the manifest manually.
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="JOIN_MANAGER_USER"
                        value={joinManagerUser}
                        onChange={(e) => setJoinManagerUser(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="Wazuh API username"
                      />
                      <Typography variant="caption" color="text.secondary">
                        This value will be used for <code>JOIN_MANAGER_USER</code> in the YAML. Leave empty to fill it manually later.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="JOIN_MANAGER_PASSWORD"
                        type="password"
                        value={joinManagerPassword}
                        onChange={(e) => setJoinManagerPassword(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="Wazuh API password"
                      />
                      <Typography variant="caption" color="text.secondary">
                        This value will be used for <code>JOIN_MANAGER_PASSWORD</code> in the YAML. Leave empty to fill it manually later.
                      </Typography>
                    </Grid>
                  </Grid>

                  <Alert severity="warning" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      <strong>Security note:</strong> Avoid committing manifests with real credentials to version control. Consider using
                      Kubernetes Secrets instead of plain text values in production.
                    </Typography>
                  </Alert>
                </StepContent>
              </Step>

              {/* Kubernetes Step 3: DaemonSet manifest */}
              <Step active={true} completed={false}>
                <StepLabel>
                  <Typography variant="h5" sx={{ fontWeight: '700', color: '#1a237e', letterSpacing: '-0.3px' }}>
                    📄 Kubernetes DaemonSet manifest
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Copy the following manifest, review the values (especially <code>JOIN_MANAGER_USER</code> and
                    <code> JOIN_MANAGER_PASSWORD</code>), save it as a YAML file and apply it with <code>kubectl</code>.
                  </Typography>

                  <Paper
                    sx={{
                      p: 2,
                      background: 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)',
                      position: 'relative',
                      borderRadius: 2,
                      border: '2px solid #667eea'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          fontFamily: '"Fira Code", "Courier New", monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          flex: 1,
                          mr: 2,
                          color: '#e0e0e0',
                          fontSize: '0.9rem',
                          lineHeight: 1.6
                        }}
                      >
                        {getKubernetesYaml()}
                      </Typography>
                      <Tooltip title={copiedCommand === 'kubernetes' ? 'Copied!' : 'Copy manifest'}>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(getKubernetesYaml(), 'kubernetes')}
                          sx={{
                            color: copiedCommand === 'kubernetes' ? '#4ade80' : '#e0e0e0',
                            background: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.2)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {copiedCommand === 'kubernetes' ? <CheckCircleIcon /> : <CopyIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>

                  <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                      Apply this manifest with <code>kubectl apply -f wazuh-agent-daemonset.yaml</code>. Ensure
                      <code> JOIN_MANAGER_USER</code> and <code> JOIN_MANAGER_PASSWORD</code> are set to your Wazuh API credentials before
                      applying.
                    </Typography>
                  </Alert>
                </StepContent>
              </Step>
            </Stepper>
          ) : (
            <Stepper 
              orientation="vertical" 
              sx={{ 
                '& .MuiStepContent-root': { 
                  borderLeft: '3px solid #e8eaf6', 
                  ml: 2, 
                  pl: 4,
                  mt: 1
                },
                '& .MuiStepLabel-root': {
                  py: 1.5
                },
                '& .MuiStepIcon-root': {
                  fontSize: '2rem',
                  '&.Mui-active': {
                    color: '#667eea'
                  },
                  '&.Mui-completed': {
                    color: '#4caf50'
                  }
                }
              }}
            >
              {/* Step 1: Select OS and Architecture */}
              <Step active={true} completed={false}>
                <StepLabel>
                  <Typography variant="h5" sx={{ fontWeight: '700', color: '#1a237e', letterSpacing: '-0.3px' }}>
                    📦 Select the package to download and install on your system
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Grid container spacing={3} sx={{ mt: 1 }}>
                    {/* OS Selection */}
                    {osOptions.map((os) => (
                      <Grid item xs={12} md={4} key={os.id}>
                        <Card 
                          sx={{ 
                            cursor: 'pointer',
                            border: selectedOS === os.id ? '3px solid #667eea' : '2px solid #e8eaf6',
                            background: selectedOS === os.id ? 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)' : 'white',
                            '&:hover': { 
                              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.2)',
                              transform: 'translateY(-4px)',
                              borderColor: '#667eea'
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            borderRadius: 3
                          }}
                          onClick={() => setSelectedOS(os.id)}
                        >
                          <CardContent sx={{ textAlign: 'center', p: 3.5 }}>
                            <Box 
                              sx={{ 
                                mb: 2, 
                                color: selectedOS === os.id ? '#667eea' : '#666',
                                transition: 'all 0.3s ease',
                                transform: selectedOS === os.id ? 'scale(1.1)' : 'scale(1)'
                              }}
                            >
                              {React.cloneElement(os.icon, { sx: { fontSize: 48 } })}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: '700', color: selectedOS === os.id ? '#667eea' : '#333' }}>
                              {os.name}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Architecture Selection */}
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: '600', color: '#1a237e' }}>
                      🔧 Select architecture:
                    </Typography>
                    <Grid container spacing={2}>
                      {getArchOptions().map((arch) => (
                        <Grid item key={arch.id}>
                          <Chip
                            label={arch.name}
                            variant={selectedArch === arch.id ? 'filled' : 'outlined'}
                            color={selectedArch === arch.id ? 'primary' : 'default'}
                            onClick={() => setSelectedArch(arch.id)}
                            sx={{ 
                              cursor: 'pointer', 
                              px: 3, 
                              py: 2.5,
                              fontSize: '0.95rem',
                              fontWeight: selectedArch === arch.id ? '600' : '500',
                              borderWidth: '2px',
                              borderRadius: 2,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                              }
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  {/* Additional Systems Note */}
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mt: 3,
                      borderRadius: 2,
                      border: '1px solid #e3f2fd',
                      '& .MuiAlert-icon': {
                        fontSize: '24px'
                      }
                    }}
                  >
                    💡 For additional systems and architectures, please check our documentation
                  </Alert>
                </StepContent>
              </Step>

              {/* Step 2: Server Address */}
              <Step active={true} completed={false}>
                <StepLabel>
                  <Typography variant="h5" sx={{ fontWeight: '700', color: '#1a237e', letterSpacing: '-0.3px' }}>
                    🌐 Server address
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    This is the address the agent uses to communicate with the server. Enter an IP address or a fully qualified domain
                    name (FQDN).
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      label="Assign a server address"
                      value={serverAddress}
                      onChange={(e) => setServerAddress(e.target.value)}
                      sx={{ minWidth: 300 }}
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      Remember server address
                    </Typography>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 3: Optional Settings */}
              <Step active={true} completed={false}>
                <StepLabel>
                  <Typography variant="h5" sx={{ fontWeight: '700', color: '#1a237e', letterSpacing: '-0.3px' }}>
                    ⚙️ Optional settings
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    By default, the deployment uses the hostname as the agent name. Optionally, you can use a different agent name in the
                    field below.
                  </Typography>
                  <TextField
                    label="Assign an agent name"
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                    placeholder="Agent name"
                    sx={{ minWidth: 300 }}
                    size="small"
                  />
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    The agent name must be unique. It can't be changed once the agent has been enrolled.
                  </Alert>
                  
                  {/* Group Management Section */}
                  <Box 
                    sx={{ 
                      mt: 4,
                      p: 3,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                      border: '2px solid #e8eaf6'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '20px'
                        }}
                      >
                        👥
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: '700', color: '#1a237e', flex: 1 }}>
                        Agent Groups Management
                      </Typography>
                      <Button
                        variant="contained"
                        size="medium"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateGroupDialogOpen(true)}
                        sx={{ 
                          textTransform: 'none',
                          borderRadius: 2,
                          px: 3,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                            boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Create Group
                      </Button>
                      <Button
                        variant="outlined"
                        size="medium"
                        startIcon={<GroupIcon />}
                        onClick={() => setAssignDialogOpen(true)}
                        sx={{ 
                          textTransform: 'none',
                          borderRadius: 2,
                          px: 3,
                          borderWidth: '2px',
                          borderColor: '#667eea',
                          color: '#667eea',
                          '&:hover': {
                            borderWidth: '2px',
                            borderColor: '#5568d3',
                            background: 'rgba(102, 126, 234, 0.08)',
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                        disabled={agents.length === 0}
                      >
                        Assign Agent
                      </Button>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                      💡 Select groups to automatically assign the agent during installation. The selected groups will be included in the
                      installation command.
                    </Typography>
                    
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Select one or more groups for installation</InputLabel>
                      <Select
                        multiple
                        value={selectedGroups}
                        onChange={(e) => setSelectedGroups(e.target.value)}
                        label="Select one or more groups for installation"
                        disabled={loadingGroups}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {loadingGroups ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Loading groups...
                          </MenuItem>
                        ) : groups.length === 0 ? (
                          <MenuItem disabled>No groups available</MenuItem>
                        ) : (
                          groups.map((group) => (
                            <MenuItem key={group.name} value={group.name}>
                              {group.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>

                    {/* Display selected groups with delete option */}
                    {selectedGroups.length > 0 && (
                      <Box 
                        sx={{ 
                          mt: 3,
                          p: 2.5,
                          borderRadius: 2,
                          background: 'white',
                          border: '2px dashed #667eea'
                        }}
                      >
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            mb: 1.5, 
                            display: 'block',
                            fontWeight: '700',
                            color: '#1a237e'
                          }}
                        >
                          ✅ Selected Groups:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                          {selectedGroups.map((groupId) => (
                            <Chip
                              key={groupId}
                              label={groupId}
                              onDelete={groupId !== 'default' ? () => handleDeleteGroup(groupId) : undefined}
                              deleteIcon={groupId !== 'default' ? <DeleteIcon /> : undefined}
                              sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '0.9rem',
                                px: 2,
                                py: 2.5,
                                '& .MuiChip-deleteIcon': {
                                  color: 'rgba(255, 255, 255, 0.8)',
                                  '&:hover': {
                                    color: 'white'
                                  }
                                },
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </StepContent>
              </Step>

              {/* Step 4: Installation Command */}
              <Step active={true} completed={false}>
                <StepLabel>
                  <Typography variant="h5" sx={{ fontWeight: '700', color: '#1a237e', letterSpacing: '-0.3px' }}>
                    💻 Run the following commands to download and install the agent
                  </Typography>
                </StepLabel>
                <StepContent>
                  {/* Configuration Summary */}
                  {(agentName || selectedGroups.length > 0) && (
                    <Alert 
                      severity="success" 
                      sx={{ 
                        mb: 3,
                        borderRadius: 2,
                        border: '2px solid #4caf50',
                        background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: '700', mb: 1, fontSize: '1rem' }}>
                        ✅ Configuration Applied:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {agentName && (
                          <li>
                            <Typography variant="body2">
                              <strong>Agent Name:</strong> {agentName}
                            </Typography>
                          </li>
                        )}
                        {selectedGroups.length > 0 && (
                          <li>
                            <Typography variant="body2">
                              <strong>Groups:</strong> {selectedGroups.join(', ')}
                            </Typography>
                          </li>
                        )}
                      </Box>
                    </Alert>
                  )}
                  
                  <Paper 
                    sx={{ 
                      p: 2, 
                      background: 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)',
                      position: 'relative',
                      borderRadius: 2,
                      border: '2px solid #667eea'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography 
                        variant="body2" 
                        component="pre" 
                        sx={{ 
                          fontFamily: '"Fira Code", "Courier New", monospace',
                          whiteSpace: 'pre-wrap', 
                          wordBreak: 'break-all',
                          flex: 1,
                          mr: 2,
                          color: '#e0e0e0',
                          fontSize: '0.9rem',
                          lineHeight: 1.6
                        }}
                      >
                        {getInstallCommand()}
                      </Typography>
                      <Tooltip title={copiedCommand === 'install' ? 'Copied!' : 'Copy command'}>
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(getInstallCommand(), 'install')}
                          sx={{ 
                            color: copiedCommand === 'install' ? '#4ade80' : '#e0e0e0',
                            background: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.2)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {copiedCommand === 'install' ? <CheckCircleIcon /> : <CopyIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>

                  <Alert 
                    severity="info" 
                    sx={{ 
                      mt: 3,
                      borderRadius: 2,
                      border: '2px solid #e3f2fd',
                      background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                      '& .MuiAlert-icon': {
                        fontSize: '28px'
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: '700', mb: 1.5, fontSize: '1rem' }}>
                      ⚠️ Requirements:
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ lineHeight: 1.8 }}>
                      • You will need <strong>administrator privileges</strong> to perform this installation.<br/>
                      • <strong>Shell Bash</strong> is required.<br/>
                      <br/>
                      💡 Keep in mind you need to run this command in a Shell Bash terminal.
                    </Typography>
                  </Alert>
                </StepContent>
              </Step>

              {/* Step 5: Start Agent */}
              <Step active={true} completed={false}>
                <StepLabel>
                  <Typography variant="h5" sx={{ fontWeight: '700', color: '#1a237e', letterSpacing: '-0.3px' }}>
                    ▶️ Start the agent
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Paper 
                    sx={{ 
                      p: 3, 
                      background: 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)',
                      position: 'relative',
                      borderRadius: 2,
                      border: '2px solid #667eea',
                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography 
                        variant="body2" 
                        component="pre" 
                        sx={{ 
                          fontFamily: '"Fira Code", "Courier New", monospace',
                          whiteSpace: 'pre-wrap',
                          flex: 1,
                          mr: 2,
                          color: '#e0e0e0',
                          fontSize: '0.9rem',
                          lineHeight: 1.6
                        }}
                      >
                        {getStartCommand()}
                      </Typography>
                      <Tooltip title={copiedCommand === 'start' ? 'Copied!' : 'Copy command'}>
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(getStartCommand(), 'start')}
                          sx={{ 
                            color: copiedCommand === 'start' ? '#4ade80' : '#e0e0e0',
                            background: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.2)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {copiedCommand === 'start' ? <CheckCircleIcon /> : <CopyIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                </StepContent>
              </Step>

            </Stepper>
          )}
        </CardContent>
      </Card>

      {/* Create Group Dialog */}
      <Dialog 
        open={createGroupDialogOpen} 
        onClose={() => setCreateGroupDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px'
            }}
          >
            ➕
          </Box>
          <Typography variant="h5" sx={{ fontWeight: '700' }}>
            Create New Group
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="e.g., web-servers, database-servers"
            helperText="Group name should be lowercase with hyphens or underscores"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#667eea'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                  borderWidth: '2px'
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setCreateGroupDialogOpen(false)} 
            disabled={creatingGroup}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateGroup} 
            variant="contained" 
            disabled={creatingGroup || !newGroupName.trim()}
            startIcon={creatingGroup ? <CircularProgress size={20} /> : <AddIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)'
              }
            }}
          >
            {creatingGroup ? 'Creating...' : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Agent to Group Dialog */}
      <Dialog 
        open={assignDialogOpen} 
        onClose={() => setAssignDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px'
            }}
          >
            👥
          </Box>
          <Typography variant="h5" sx={{ fontWeight: '700' }}>
            Assign Agent to Group
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3 }}>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Select Agent</InputLabel>
            <Select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              label="Select Agent"
            >
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name} ({agent.ip})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Select Groups</InputLabel>
            <Select
              multiple
              value={selectedGroups}
              onChange={(e) => setSelectedGroups(e.target.value)}
              label="Select Groups"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {groups.map((group) => (
                <MenuItem key={group.name} value={group.name}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mt: 2 }}>
            The agent will be assigned to the selected groups. Existing group assignments will be replaced.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assigningAgent}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssignAgent} 
            variant="contained" 
            disabled={assigningAgent || !selectedAgent || selectedGroups.length === 0}
            startIcon={assigningAgent ? <CircularProgress size={20} /> : <GroupIcon />}
          >
            {assigningAgent ? 'Assigning...' : 'Assign Agent'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Container>
  );
};

export default DeployAgent;

